import type { AltchaOperation } from "@/features/post/lib/altchaChallengeController";

export type AltchaState =
  | "error"
  | "expired"
  | "unverified"
  | "verified"
  | "verifying";

export const ALTCHA_STATUS_MESSAGES: Record<AltchaState, string> = {
  error: "書き込み認証に失敗しました。再試行してください。",
  expired: "書き込み認証の期限が切れました。再試行してください。",
  unverified: "",
  // 認証中と認証完了は widget 自身の視覚状態で伝わるため、テキスト行を持たず縦スペースも詰める。
  verified: "",
  verifying: "",
};

type AltchaEventHandlers = {
  expired: EventListener;
  statechange: EventListener;
  verified: EventListener;
};

/**
 * ALTCHA widget の `auto` は検証を自動で始める契機を決める
 * (`off` / `onfocus` / `onload` / `onsubmit`)。
 *
 * `off` を選ぶ理由:
 * - widget 内蔵の `onfocus` は state === UNVERIFIED だけを見て verify を起動する。
 *   セッション制廃止後は「フォームを開くたびに新しい fresh PoW が要る」ので
 *   契機は自前で制御し、SP はモーダル open、PC はフォーム focusin で叩き分ける。
 *   1 payload を widget 内 state 遷移で消化する点は変わらない。
 * - `onload` は閲覧するだけの利用者にも PoW を強いるため採らない
 *   (掲示板は閲覧が大半を占める)。
 * - `onsubmit` は widget 側が submit を横取りするため、
 *   既存の React 送信ライフサイクル (submissionLock / useSubmitPost) と競合する。
 *
 * 期限切れや契機を取りこぼした場合も、送信時の `widget.verify()` が保険として働く。
 */
type AltchaWidgetConfiguration = {
  auto: "off";
  challenge: string;
  display: "standard";
  fetch: typeof fetch;
  hideFooter: boolean;
  humanInteractionSignature: false;
  language: "ja";
  type: "checkbox";
  verifyUrl: null;
  workers: number;
};

/** PC 側で許す PoW ワーカーの上限。既定の hardwareConcurrency 丸ごとは使わない。 */
const MAX_ALTCHA_WORKERS = 4;
/** hardwareConcurrency を読めない環境で使う本数 (altcha 自身の既定値と揃える)。 */
const FALLBACK_ALTCHA_WORKERS = 2;

/**
 * PoW を解く Web Worker の並列数を決める。
 *
 * altcha 3.2.1 の既定は `navigator.hardwareConcurrency` (最大16) をそのまま使う。
 * iOS Safari は `navigator.deviceMemory` を実装しないため、ライブラリ内の
 * 「メモリ4GB以下なら4本まで」という抑制に入らず、コア数ぶん丸ごと起動する。
 * ワーカー生成は直列 await で行われ、ライブラリ自身が OOM 時に並列数を半減させる
 * フォールバックを持つほど負荷が高い。
 *
 * タッチ端末は 1 本に絞る。SP は「モーダル open の瞬間」に PoW を先行させる方針で
 * ソフトキーボード表示アニメーションと重なる直接の focusin 契機を廃した。
 * maxNumber=100,000 のチャレンジは 1 本でも 1 秒未満で解けるため、SP でも投稿導線を
 * 途切れさせない。
 */
export function resolveAltchaWorkerConcurrency(
  hardwareConcurrency: number | undefined,
  isCoarsePointer: boolean,
): number {
  if (isCoarsePointer) return 1;
  const cores =
    typeof hardwareConcurrency === "number" &&
    Number.isFinite(hardwareConcurrency) &&
    hardwareConcurrency >= 1
      ? Math.floor(hardwareConcurrency)
      : FALLBACK_ALTCHA_WORKERS;
  return Math.min(MAX_ALTCHA_WORKERS, cores);
}

/** ポインタが粗い (指で操作する) 端末かを判定する。 */
export function detectCoarsePointer(): boolean {
  return globalThis.matchMedia?.("(pointer: coarse)").matches ?? false;
}

export function createAltchaWidgetConfiguration(
  operation: AltchaOperation,
  apiBase: string,
  challengePath: (operation: AltchaOperation, apiBase: string) => string,
  fetchChallenge: typeof fetch,
  workers: number,
  hideFooter = false,
): AltchaWidgetConfiguration {
  return {
    auto: "off" as const,
    challenge: challengePath(operation, apiBase),
    display: "standard" as const,
    fetch: fetchChallenge,
    hideFooter,
    humanInteractionSignature: false,
    language: "ja" as const,
    type: "checkbox" as const,
    verifyUrl: null,
    workers,
  };
}

/**
 * `focusin` を購読する前にフォームへフォーカスが入っていたかを判定する。
 *
 * 購読の開始は widget の configure 完了後になるため、それ以前に発生した focus は
 * 取りこぼす。購読の登録直後にこの判定で最初の1回を補う。
 */
export function hasFocusWithinHostForm(
  element: HTMLElement,
  activeElement: Element | null,
): boolean {
  const form = element.closest("form");
  return (
    form !== null && activeElement !== null && form.contains(activeElement)
  );
}

type AltchaAutoVerifierOptions = {
  isActive: () => boolean;
  onError: () => void;
  verify: () => Promise<unknown>;
};

export type AltchaAutoVerifier = {
  /** 期限切れ等で検証をやり直せる状態に戻す (次の契機で再起動できるようにする)。 */
  release: () => void;
  /** 契機の検証を1回だけ要求する。 */
  request: () => void;
  /** 別経路 (再試行ボタン) で検証を始めたことを記録し、契機の二重起動を防ぐ。 */
  start: () => void;
};

/**
 * 自動検証。セッション制廃止後は verify を無条件に1回だけ起動する。
 * SP はモーダル open、PC はフォーム focusin から `request()` される。
 * 起動済みの間は再要求を無視し、多重 verify を防ぐ。
 */
export function createAltchaAutoVerifier({
  isActive,
  onError,
  verify,
}: AltchaAutoVerifierOptions): AltchaAutoVerifier {
  let started = false;

  return {
    release: (): void => {
      started = false;
    },
    request: (): void => {
      if (started || !isActive()) return;
      started = true;
      void (async (): Promise<void> => {
        try {
          await verify();
        } catch {
          if (isActive()) onError();
        }
      })();
    },
    start: (): void => {
      started = true;
    },
  };
}

/**
 * widget を含むフォームの `focusin` を購読し、契機の検証要求を配線する。
 * フォームの外に置かれた widget は自動検証の対象にしない。
 * PC (fine pointer) 用。SP は AltchaWidget 内で active 遷移をトリガーにする。
 */
export function registerAltchaFocusTrigger(
  element: HTMLElement,
  activeElement: Element | null,
  request: () => void,
): () => void {
  const form = element.closest("form");
  if (!form) return (): void => undefined;

  const handleFocusIn = (): void => {
    request();
  };
  form.addEventListener("focusin", handleFocusIn);
  if (hasFocusWithinHostForm(element, activeElement)) request();

  return (): void => {
    form.removeEventListener("focusin", handleFocusIn);
  };
}

export function registerAltchaWidgetListeners(
  element: HTMLElement,
  handlers: AltchaEventHandlers,
): () => void {
  element.addEventListener("verified", handlers.verified);
  element.addEventListener("statechange", handlers.statechange);
  element.addEventListener("expired", handlers.expired);

  return (): void => {
    element.removeEventListener("verified", handlers.verified);
    element.removeEventListener("statechange", handlers.statechange);
    element.removeEventListener("expired", handlers.expired);
  };
}
